import { useCallback } from 'react';

import createActiveDeliveryEvent from 'src/modules/Crypto/lib/activeDeliveryEvents/createActiveDeliveryEvent';
import {
  TokenData,
  useActiveDeliveryState
} from 'src/modules/DocumentsPrivate/components/documentSignatureForm/context/ActiveDeliveryStateProvider';
import useAddEvent from 'src/modules/DocumentsPrivate/hooks/useAddEvent';
import { GetPartnerEncryptionKeyResponse } from 'src/modules/DocumentsPrivate/types';

type useProcessDocumentProps = {
  uuid: string;
  partnerEncryptionKey?: GetPartnerEncryptionKeyResponse;
  publisherId: string;
  documentBase58Hash?: string;
  blockchainAddress?: string;
};

const useSendActiveDeliveryEvent = ({
  uuid,
  partnerEncryptionKey,
  publisherId,
  documentBase58Hash,
  blockchainAddress
}: useProcessDocumentProps) => {
  const {
    isDocumentReceived,
    isDocumentAcknowledged,
    confirmDocumentReceived,
    confirmDocumentAcknowledged,
    getConfirmationOfReceiptTokenData,
    getConfirmationOfAcknowledgementTokenData
  } = useActiveDeliveryState();

  const { mutate: addEvent } = useAddEvent();

  const createAndSendEvent = useCallback(
    async (
      tokenData: TokenData,
      eventType: string,
      blockchainAddress: string,
      partnerEncryptionKey: GetPartnerEncryptionKeyResponse
    ) => {
      const { tokenIdBase58, tokenBody } = tokenData;

      const encryptedSensitiveEventData = await createActiveDeliveryEvent({
        documentHash: documentBase58Hash,
        uuid,
        tokenIdBase58,
        partnerEncryptionKey
      });

      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

      addEvent({
        publisherId,
        signedEventToken: tokenBody,
        encryptedSensitiveEventData,
        retentionDate: oneYearFromNow.toISOString(),
        blockchainAddress,
        eventType
      });
    },
    [addEvent, documentBase58Hash, publisherId, uuid]
  );

  const sendAcknowledgementEvent = useCallback(() => {
    const tokenData = getConfirmationOfAcknowledgementTokenData();
    if (
      partnerEncryptionKey &&
      !isDocumentAcknowledged &&
      tokenData &&
      blockchainAddress
    ) {
      createAndSendEvent(
        tokenData,
        'CONFIRMATION_OF_ACKNOWLEDGEMENT',
        blockchainAddress,
        partnerEncryptionKey
      );
    }
    confirmDocumentAcknowledged();
  }, [
    getConfirmationOfAcknowledgementTokenData,
    partnerEncryptionKey,
    isDocumentAcknowledged,
    blockchainAddress,
    confirmDocumentAcknowledged,
    createAndSendEvent
  ]);

  const sendReceiptEvent = useCallback(() => {
    const tokenData = getConfirmationOfReceiptTokenData();

    if (tokenData && blockchainAddress && partnerEncryptionKey) {
      createAndSendEvent(
        tokenData,
        'CONFIRMATION_OF_RECEIPT',
        blockchainAddress,
        partnerEncryptionKey
      );
    }
    if (
      partnerEncryptionKey &&
      !isDocumentReceived &&
      tokenData &&
      blockchainAddress
    ) {
      confirmDocumentReceived();
      createAndSendEvent(
        tokenData,
        'CONFIRMATION_OF_RECEIPT',
        blockchainAddress,
        partnerEncryptionKey
      );
    }
  }, [
    getConfirmationOfReceiptTokenData,
    partnerEncryptionKey,
    isDocumentReceived,
    blockchainAddress,
    confirmDocumentReceived,
    createAndSendEvent
  ]);

  return { sendAcknowledgementEvent, sendReceiptEvent };
};

export default useSendActiveDeliveryEvent;
